'use client';

import { TrendingUp, ChevronRight, LayoutDashboard, User } from 'lucide-react';

interface InvestorHubMobileWidgetProps {
  className?: string;
}

// Mock data
const mockUser = {
  name: 'Carlos',
  greeting: 'Buenas tardes',
};

const mockDashboard = {
  totalInvested: 125000,
  pendingAmount: 35000,
  confirmedAmount: 90000,
  investmentCount: 4,
};

const mockPendingContract = {
  promoterName: 'Aurora Films',
  amount: 35000,
  type: 'financing',
};

const mockRecentInvestments = [
  {
    id: '1',
    promoterName: 'Aurora Films',
    amount: 35000,
    date: 'Hace 2 dias',
    initials: 'AF',
  },
  {
    id: '2',
    promoterName: 'Cine Capital',
    amount: 50000,
    date: 'Hace 1 semana',
    initials: 'CC',
  },
];

// Format currency helper
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

export function InvestorHubMobileWidget({ className }: InvestorHubMobileWidgetProps) {
  // iPhone aspect ratio: 19.5:9 = ~2.17:1 (height:width)
  // Width 280px -> Height ~607px, but with bezel padding we use ~560px for screen
  const phoneWidth = 280;
  const screenHeight = 560;

  return (
    <div className={className}>
      {/* Phone Frame - Realistic iPhone proportions */}
      <div className="relative mx-auto" style={{ width: phoneWidth }}>
        {/* Phone bezel */}
        <div className="relative bg-zinc-900 rounded-[2.5rem] p-2 shadow-2xl">
          {/* Dynamic Island */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-6 bg-zinc-900 rounded-b-2xl z-20" />

          {/* Screen - Fixed realistic height */}
          <div
            className="relative bg-zinc-50 rounded-[2rem] overflow-hidden"
            style={{ height: screenHeight }}
          >
            {/* Status bar - iOS style */}
            <div className="flex items-center justify-between px-6 py-2 bg-zinc-50">
              <span className="text-[10px] font-semibold text-zinc-900">9:41</span>
              <div className="flex items-center gap-1.5">
                {/* Cellular signal - 4 bars like real iOS */}
                <div className="flex items-end gap-[2px]">
                  <div className="w-[3px] h-[4px] bg-zinc-900 rounded-[1px]" />
                  <div className="w-[3px] h-[6px] bg-zinc-900 rounded-[1px]" />
                  <div className="w-[3px] h-[8px] bg-zinc-900 rounded-[1px]" />
                  <div className="w-[3px] h-[10px] bg-zinc-900 rounded-[1px]" />
                </div>
                {/* Battery */}
                <div className="flex items-center">
                  <div className="w-[22px] h-[10px] border border-zinc-900 rounded-[3px] relative">
                    <div className="absolute inset-[2px] right-[3px] bg-zinc-900 rounded-[1px]" />
                  </div>
                  <div className="w-[1px] h-[4px] bg-zinc-900 rounded-r-full ml-[1px]" />
                </div>
              </div>
            </div>

            {/* App Content - Scrollable area */}
            <div className="px-4 pt-2 pb-20 space-y-4 bg-zinc-50">
              {/* Greeting Section */}
              <div>
                <p className="text-zinc-500 text-[11px]">{mockUser.greeting},</p>
                <h1 className="text-base font-semibold text-zinc-900">{mockUser.name}</h1>
              </div>

              {/* Balance Card */}
              <div className="relative bg-white rounded-2xl p-4 shadow-[0_2px_12px_-2px_rgba(0,0,0,0.08),0_0_0_1px_rgba(0,0,0,0.02)] overflow-hidden">
                {/* Subtle violet gradient accent */}
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-violet-500/10 via-violet-500/5 to-transparent rounded-full -translate-y-1/2 translate-x-1/2" />

                <div className="relative">
                  {/* Total Label with investment count */}
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-zinc-500 text-[9px] font-medium uppercase tracking-wide">
                      Inversion Total
                    </p>
                    <div className="flex items-center gap-0.5 text-violet-600 bg-violet-50 px-1.5 py-0.5 rounded-full">
                      <TrendingUp size={8} />
                      <span className="text-[9px] font-medium">
                        {mockDashboard.investmentCount} inv.
                      </span>
                    </div>
                  </div>

                  {/* Main Amount */}
                  <p className="text-2xl font-bold text-zinc-900 tracking-tight mb-3">
                    {formatCurrency(mockDashboard.totalInvested)}
                  </p>

                  {/* Secondary Stats */}
                  <div className="flex gap-2">
                    {/* Pending */}
                    <div className="flex-1 bg-gradient-to-br from-zinc-50 to-zinc-100/50 rounded-xl p-2 border border-zinc-100">
                      <p className="text-[8px] text-zinc-500 font-medium uppercase tracking-wide mb-0.5">
                        Pendiente
                      </p>
                      <p className="text-sm font-semibold text-zinc-900">
                        {formatCurrency(mockDashboard.pendingAmount)}
                      </p>
                    </div>

                    {/* Confirmed */}
                    <div className="flex-1 bg-gradient-to-br from-zinc-50 to-zinc-100/50 rounded-xl p-2 border border-zinc-100">
                      <p className="text-[8px] text-zinc-500 font-medium uppercase tracking-wide mb-0.5">
                        Confirmada
                      </p>
                      <p className="text-sm font-semibold text-zinc-900">
                        {formatCurrency(mockDashboard.confirmedAmount)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pending Actions Section */}
              <div className="space-y-2">
                {/* Header */}
                <div className="flex items-center gap-1 px-0.5">
                  <h2 className="text-[11px] font-semibold text-zinc-900">Acciones pendientes</h2>
                  <span className="w-3.5 h-3.5 bg-violet-500 text-white text-[8px] font-bold flex items-center justify-center rounded">
                    1
                  </span>
                </div>

                {/* Contracts Card - THE KEY "AHA MOMENT" - Using real image */}
                <div className="relative bg-white/90 backdrop-blur-sm rounded-xl p-3 overflow-visible shadow-sm border border-zinc-100">
                  {/* Header: Real contracts.png Image + Title/Subtitle */}
                  <div className="flex items-start gap-1">
                    {/* Overflowing 3D Image - using the real asset */}
                    <div className="relative flex-shrink-0 -ml-4 -mt-10">
                      <img
                        src="/assets/contracts.png"
                        alt="Contracts"
                        className="w-24 h-24 object-contain relative z-10"
                      />
                      {/* Ground shadow */}
                      <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-16 h-3 bg-black/15 rounded-full blur-sm" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0 pt-1">
                      <h3 className="text-sm font-bold text-zinc-900">Contratos pendientes</h3>
                      <p className="text-[10px] text-zinc-500 mt-0.5">
                        Tienes 1 contrato por firmar
                      </p>
                    </div>
                  </div>

                  {/* Action Item - Sign Contract */}
                  <div className="mt-1">
                    <button className="w-full flex items-center justify-between gap-2 py-2 px-2.5 rounded-lg bg-zinc-50 hover:bg-zinc-100 transition-all duration-150 text-left">
                      <span className="text-[11px] font-medium text-zinc-700 truncate flex-1">
                        {mockPendingContract.promoterName}
                      </span>
                      <span className="text-[11px] font-semibold text-zinc-900">
                        {formatCurrency(mockPendingContract.amount)}
                      </span>
                      <ChevronRight size={12} className="text-zinc-400 flex-shrink-0" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Recent Investments Section */}
              <div className="space-y-2">
                {/* Header */}
                <div className="flex items-center justify-between px-0.5">
                  <h2 className="text-[11px] font-semibold text-zinc-900">Inversiones Recientes</h2>
                </div>

                {/* Activity Items - 2 items to fit */}
                <div className="bg-white rounded-xl border border-zinc-100 shadow-sm overflow-hidden">
                  {mockRecentInvestments.map((investment, index) => (
                    <button
                      key={investment.id}
                      className={`w-full flex items-center gap-2 px-3 py-2 hover:bg-zinc-50 transition-colors text-left ${
                        index !== mockRecentInvestments.length - 1 ? 'border-b border-zinc-100' : ''
                      }`}
                    >
                      {/* Avatar with initials */}
                      <div className="w-7 h-7 bg-zinc-200 rounded flex items-center justify-center flex-shrink-0">
                        <span className="text-[9px] font-medium text-zinc-600">
                          {investment.initials}
                        </span>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] font-medium text-zinc-900 truncate">
                          {investment.promoterName}
                        </p>
                        <p className="text-[9px] text-zinc-500">{investment.date}</p>
                      </div>

                      {/* Amount */}
                      <div className="text-right flex-shrink-0">
                        <p className="text-[11px] font-medium text-zinc-900">
                          {formatCurrency(investment.amount)}
                        </p>
                      </div>

                      {/* Chevron */}
                      <ChevronRight size={12} className="text-zinc-300 flex-shrink-0" />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Bottom Navigation */}
            <div className="absolute bottom-0 inset-x-0 bg-white border-t border-zinc-200">
              <div className="flex items-center justify-center h-12 gap-12">
                {/* Dashboard - Active */}
                <div className="flex flex-col items-center justify-center px-3 py-1">
                  <LayoutDashboard size={18} className="text-zinc-900" />
                  <span className="mt-0.5 text-[9px] font-medium text-zinc-900">Dashboard</span>
                </div>

                {/* Profile */}
                <div className="flex flex-col items-center justify-center px-3 py-1">
                  <User size={18} className="text-zinc-400" />
                  <span className="mt-0.5 text-[9px] font-medium text-zinc-500">Perfil</span>
                </div>
              </div>
              {/* Home indicator */}
              <div className="flex justify-center pb-1">
                <div className="w-24 h-1 bg-zinc-900 rounded-full" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default InvestorHubMobileWidget;
